import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Grid,
  makeStyles,
  Typography,
  ButtonGroup,
  useTheme,
  useMediaQuery,
  Checkbox,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { emptyObject, emptyString } from "constants/defaultValues";
import { Autocomplete, CustomTextField, DateTime } from "@totalsoft/rocket-webapp-components-deprecated";
import { get, sequence, set } from "@totalsoft/change-tracking-react";
import productPageStyle from "features/products/styles";
import { emptyArray } from "constants/defaultValues";
import { getErrors, isValid } from "@totalsoft/pure-validations-react";
import { onNumericTextFieldChange, onCheckBoxChange, onLensSliderChange } from "utils/propertyChangeAdapters";
import ServiceContainer from "./ServiceComponent";
import SimplebarWrapper from "features/common/components/ScrollbarWrapper";
import { useQueryWithErrorHandling } from "hooks/errorHandling";
import { INIT_PARAM_VALUE_QUERY } from "features/common/queries";
import useUserData from "hooks/useUserData";
import { computeServiceAmount, computeServicePercent } from "features/products/rules";
import { serviceCalculationTypeId } from "../../constants";
import SimpleSliderWrapper from "../SimpleSliderWrapper";

const useStyles = makeStyles(productPageStyle);

function AdvancedEditProductParams(props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const styleProps = useMemo(() => ({ fullScreen }), [fullScreen]);
  const classes = useStyles(styleProps);
  const [interestPercentWithSixDecimals, setInterestPercentWithSixDecimals] = React.useState(false);

  const { productPeriodicities, productScheduleTypes, autoAddedInsuranceTypes } = props;
  const { productLens, quote, productValidation, settingsLens } = props;
  const product = productLens |> get || emptyObject;

  const settings = settingsLens |> get || emptyObject;

  const useRentalDownPayment = productLens.useRentalDownPayment |> get;
  const useDownPaymentPercent = settingsLens.useDownPaymentPercent |> get;
  const useResidualPercent = settingsLens.useResidualPercent |> get;
  const useInterestPercent = settingsLens.useInterestPercent |> get;
  const { allowInsurance } = useUserData();

  useQueryWithErrorHandling(INIT_PARAM_VALUE_QUERY, {
    variables: { paramName: "VCP_UseInterestPercentWithSixDecimalPlaces" },
    skip: !settings?.enableInterestSlider,
    onCompleted: (data) => {
      data?.initializationParamValue?.value?.toLowerCase() === "true"
        ? setInterestPercentWithSixDecimals(true)
        : setInterestPercentWithSixDecimals(false);
    },
  });

  const handleActions = useCallback(
    (event) => {
      switch (event.currentTarget.id) {
        case "down-payment-percent":
          (settingsLens.useDownPaymentPercent |> set)(true);
          break;
        case "down-payment-abs":
          (settingsLens.useDownPaymentPercent |> set)(false);
          break;
        case "balloon-percent":
          (settingsLens.useResidualPercent |> set)(true);
          break;
        case "balloon-abs":
          (settingsLens.useResidualPercent |> set)(false);
          break;
        case "interest-percent":
          (settingsLens.useInterestPercent |> set)(true);
          break;
        case "interest-abs":
          (settingsLens.useInterestPercent |> set)(false);
          break;
      }
    },
    [settingsLens.useDownPaymentPercent, settingsLens.useResidualPercent, settingsLens.useInterestPercent]
  );

  const handleAddService = useCallback(
    ({ serviceTypeId }) => {
      const quoteProductServices = productLens.quoteProductServices |> get || emptyArray;
      if (quoteProductServices.some((el) => el.serviceTypeId === serviceTypeId)) return;

      const serviceType = (settingsLens.productServiceTypes |> get).find((x) => x.serviceTypeId === serviceTypeId);
      const addedQuoteProductService = {
        id: null,
        quoteProductId: productLens.quoteProductId |> get,
        serviceTypeId: serviceType.serviceTypeId,
        serviceTypeCode: serviceType.serviceCode,
        serviceTypeName: serviceType.serviceName,
        calcTypeId: serviceType.calcTypeId,
        servicePercent: serviceType.calcTypeId === serviceCalculationTypeId.ExactValue ? 0 : serviceType.defaultPercent,
        serviceAmount: serviceType.defaultValue,
        duration: productLens.duration |> get,
        periodicity: serviceType.periodicity,
        financedYN: serviceType.financedYN,
        readOnly: serviceType.readOnly,
      };

      const updatedQuoteProductServices = [...quoteProductServices, addedQuoteProductService];

      if (serviceType.calcTypeId !== serviceCalculationTypeId.ExactValue) {
        const totalAmount =
          productLens.showAmountsWithVAT |> get
            ? productLens.totalAmountWithVAT |> get
            : productLens.totalAmount |> get;

        const financedAmount =
          productLens.showAmountsWithVAT |> get
            ? productLens.financedAmountWithVAT |> get
            : productLens.financedAmount |> get;

        if (serviceType.defaultPercent) {
          const newAmount = computeServiceAmount(
            serviceType.defaultPercent,
            totalAmount,
            financedAmount,
            serviceType,
            updatedQuoteProductServices
          );
          addedQuoteProductService.serviceAmount = newAmount;
        } else if (serviceType.defaultValue) {
          const newPercent = computeServicePercent(
            serviceType.defaultValue,
            totalAmount,
            financedAmount,
            serviceType,
            updatedQuoteProductServices
          );
          addedQuoteProductService.servicePercent = newPercent;
        }
      }
      (productLens.quoteProductServices |> set)(updatedQuoteProductServices);
    },
    [productLens, settingsLens.productServiceTypes]
  );

  const handleDeleteService = useCallback(
    (serviceTypeId) => {
      const currentServices = productLens.quoteProductServices |> get;
      const newServices = currentServices.filter((el) => el.serviceTypeId !== serviceTypeId);
      (productLens.quoteProductServices |> set)([...newServices]);
    },
    [productLens.quoteProductServices]
  );

  const handleScheduleTypeChange = useCallback(
    (scheduleTypeId) => {
      (productLens.scheduleTypeId |> set)(scheduleTypeId);
      const scheduleType = productScheduleTypes.find((x) => x.id === scheduleTypeId);
      (productLens.scheduleIsConfigurable |> set)(scheduleType?.isConfigurable);
      (settingsLens.years |> sequence).forEach((yearLens) => {
        (yearLens.months |> sequence).forEach((monthLens) => (monthLens.selected |> set)(false));
      });
      (productLens.cellParamsConfigurations |> set)([]);
    },
    [
      productLens.scheduleTypeId,
      productScheduleTypes,
      productLens.scheduleIsConfigurable,
      settingsLens.years,
      productLens.cellParamsConfigurations,
    ]
  );

  return (
    <SimplebarWrapper className={classes.scrollbar}>
      <Grid container spacing={2} className={classes.generalGridContainer}>
        <Grid item xs container>
          <Grid item xs={12}>
            <Typography variant="subtitle2" className={classes.typography}>
              {t("Quote.Assets")}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {quote.assetNo + " " + (quote.assetNo > 1 ? t("Asset.Items") : t("Asset.Item"))}
          </Grid>
        </Grid>

        <Grid item xs container>
          <Grid item xs={12}>
            <Typography variant="subtitle2" className={classes.typography}>
              {product?.useVatFinancing
                ? product?.showAmountsWithVAT
                  ? t("Product.NetPurchasePriceWithVAT")
                  : t("Product.NetPurchasePrice")
                : product?.showAmountsWithVAT
                ? t("Product.TotalAmountWithVAT")
                : t("Product.TotalAmount")}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {quote.currencyCode +
              " " +
              t("DECIMAL2", {
                number: product?.showAmountsWithVAT ? product?.totalAmountWithVAT : product?.totalAmount,
              })}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            options={productScheduleTypes.filter((x) => x.isConfigurable)}
            value={product?.scheduleTypeId || emptyString}
            onChange={handleScheduleTypeChange}
            valueKey="id"
            labelKey="caption"
            label={t("EditProduct.Schedule")}
            simpleValue={true}
            noOptionsText={t("General.AutocompleteNoOptions")}
          />
        </Grid>

        {product?.useVatFinancing && (
          <Grid item container spacing={1} alignItems="center">
            <Grid item xs={3}>
              <Typography className={classes.vatFinancingLabel}>{t("Product.VatFinancing")}</Typography>
            </Grid>
            <Grid item xs={9}>
              <Checkbox
                checked={(productLens.vatFinancing |> get) || false}
                onChange={productLens.vatFinancing |> set |> onCheckBoxChange}
                size="small"
              />
            </Grid>
            {(productLens.vatFinancing |> get) && (
              <Grid item container alignItems="center">
                <Grid item xs={5}>
                  <Typography className={classes.vatFinancingLabel}>{t("FinalPurchasePrice")}</Typography>
                </Grid>
                <Grid item xs={7}>
                  <input
                    className={classes.input}
                    type="text"
                    value={product?.finalPurchasePrice?.toFixed(2)}
                    disabled
                  />
                </Grid>
                <Grid item xs={5}>
                  <Typography className={classes.vatFinancingLabel}>{t("FinalDownPayment")}</Typography>
                </Grid>
                <Grid item xs={7}>
                  <input
                    className={classes.input}
                    type="text"
                    value={product?.finalDownPaymentAmount?.toFixed(2)}
                    disabled
                  />
                </Grid>
                <Grid item xs={5}>
                  <Typography className={classes.vatFinancingLabel}>{t("FinancedVATAmount")}</Typography>
                </Grid>
                <Grid item xs={7}>
                  <input
                    className={classes.input}
                    type="text"
                    value={product?.financedVatAmount?.toFixed(2)}
                    disabled
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        )}

        {useRentalDownPayment && (
          <Grid item xs={12}>
            <CustomTextField
              label={t("Product.RentalDownPayment")}
              fullWidth
              isNumeric={true}
              onChange={productLens.rentalDownPaymentAmount |> set |> onNumericTextFieldChange}
              value={product?.rentalDownPaymentAmount || 0}
              startAdornment={<Typography variant="h6">{quote?.currencyCode}</Typography>}
            />
          </Grid>
        )}

        {!useRentalDownPayment && settings?.enableDownPaymentSlider && (
          <Grid item container spacing={2} alignItems="center">
            <Grid item xs={8}>
              {useDownPaymentPercent === true ? (
                <SimpleSliderWrapper
                  fullWidth
                  label={`${
                    product?.showAmountsWithVAT ? t("Product.DownPaymentWithVAT") : t("Product.DownPayment")
                  } | ${t("DECIMAL2", {
                    number: product?.downPaymentPercent,
                  })}% (${quote?.currencyCode} ${
                    product?.showAmountsWithVAT
                      ? t("DECIMAL2", { number: product?.downPaymentAmountWithVAT })
                      : t("DECIMAL2", { number: product?.downPaymentAmount })
                  })`}
                  min={product.downPaymentMinPercent}
                  max={product.downPaymentMaxPercent}
                  value={product.downPaymentPercent}
                  onChange={onLensSliderChange(productLens.downPaymentPercent)}
                  showSliderLimits
                  error={!isValid(productValidation?.downPaymentPercent)}
                  helperText={getErrors(productValidation?.downPaymentPercent)}
                  decimalScale={2}
                  step={1}
                  classes={{ root: classes.sliderRoot, thumb: classes.sliderThumb }}
                />
              ) : (
                <CustomTextField
                  fullWidth
                  label={`${
                    product?.showAmountsWithVAT ? t("Product.DownPaymentWithVAT") : t("Product.DownPayment")
                  } | ${t("DECIMAL2", {
                    number: product?.downPaymentPercent,
                  })}% (${quote?.currencyCode} ${
                    product?.showAmountsWithVAT
                      ? t("DECIMAL2", { number: product?.downPaymentAmountWithVAT })
                      : t("DECIMAL2", { number: product?.downPaymentAmount })
                  })`}
                  isNumeric={true}
                  onChange={
                    product?.showAmountsWithVAT
                      ? productLens.downPaymentAmountWithVAT |> set |> onNumericTextFieldChange
                      : productLens.downPaymentAmount |> set |> onNumericTextFieldChange
                  }
                  value={product?.showAmountsWithVAT ? product?.downPaymentAmountWithVAT : product?.downPaymentAmount}
                  startAdornment={<Typography variant="h6">{quote.currencyCode}</Typography>}
                  error={
                    !isValid(
                      product?.showAmountsWithVAT
                        ? productValidation?.downPaymentAmountWithVAT
                        : productValidation?.downPaymentAmount
                    )
                  }
                  helperText={getErrors(
                    product?.showAmountsWithVAT
                      ? productValidation?.downPaymentAmountWithVAT
                      : productValidation?.downPaymentAmount
                  )}
                />
              )}
            </Grid>
            <Grid item xs={4}>
              <ButtonGroup className={classes.rightAlign}>
                <Button
                  id="down-payment-percent"
                  variant="contained"
                  color={useDownPaymentPercent === true ? "primary" : "secondary"}
                  onClick={handleActions}
                >
                  {t("General.Percent")}
                </Button>
                <Button
                  id="down-payment-abs"
                  variant="contained"
                  color={useDownPaymentPercent === false ? "primary" : "secondary"}
                  onClick={handleActions}
                >
                  {t("General.Abs")}
                </Button>
              </ButtonGroup>
            </Grid>
          </Grid>
        )}

        <Grid item xs={12}>
          <SimpleSliderWrapper
            label={t("EditProduct.Term") + " | " + product.duration + " " + t("EditProduct.Months")}
            min={product.minDuration}
            max={product.maxDuration}
            value={product.duration}
            aria-valuetext="termSlider"
            onChange={onLensSliderChange(productLens.duration)}
            fullWidth
            showSliderLimits
            decimalScale={0}
            step={1}
            classes={{ root: classes.sliderRoot, thumb: classes.sliderThumb }}
          />
        </Grid>

        {settings?.enableInterestSlider && (
          <Grid item container spacing={2} alignItems="center">
            <Grid item xs={8}>
              {useInterestPercent === true ? (
                <SimpleSliderWrapper
                  fullWidth
                  label={t("Product.Interest") + " (" + t("General.Percent") + ")"}
                  min={settingsLens.interestMinPercent |> get}
                  max={settingsLens.interestMaxPercent |> get}
                  value={product.totalInterestPercent}
                  onChange={onLensSliderChange(productLens.totalInterestPercent)}
                  showSliderLimits={true}
                  step={interestPercentWithSixDecimals ? 0.000001 : 0.01}
                  decimalScale={interestPercentWithSixDecimals ? 6 : 2}
                  error={!isValid(productValidation?.totalInterestPercent)}
                  helperText={getErrors(productValidation?.totalInterestPercent)}
                  classes={{ root: classes.sliderRoot, thumb: classes.sliderThumb }}
                />
              ) : (
                <CustomTextField
                  fullWidth
                  label={t("Product.Interest") + " (" + t("General.Percent") + ")"}
                  isNumeric={true}
                  value={product.totalInterestPercent || emptyString}
                  onChange={productLens.totalInterestPercent |> set |> onNumericTextFieldChange}
                  error={!isValid(productValidation?.totalInterestPercent)}
                  helperText={getErrors(productValidation?.totalInterestPercent)}
                  inputProps={{ decimalScale: interestPercentWithSixDecimals ? 6 : 2, thousandSeparator: true }}
                />
              )}
            </Grid>
            <Grid item xs={4}>
              <ButtonGroup className={classes.rightAlign}>
                <Button
                  id="interest-percent"
                  variant="contained"
                  color={useInterestPercent === true ? "primary" : "secondary"}
                  onClick={handleActions}
                >
                  {t("General.Percent")}
                </Button>
                <Button
                  id="interest-abs"
                  variant="contained"
                  color={useInterestPercent === false ? "primary" : "secondary"}
                  onClick={handleActions}
                >
                  {t("General.Abs")}
                </Button>
              </ButtonGroup>
            </Grid>
          </Grid>
        )}

        {settings?.enableDealerSubsidyPreview && (
          <Grid item xs={12}>
            {!(productLens.subsidyValue |> get) && (
              <CustomTextField disabled label={t("Product.SubsidyForecast") + t("Product.NotCalculated")} fullWidth />
            )}

            {(productLens.subsidyValue |> get) && (
              <CustomTextField
                isNumeric={true}
                startAdornment={<Typography variant="h6">{quote?.currencyCode}</Typography>}
                label={t("Product.SubsidyForecast")}
                value={productLens.subsidyValue |> get}
                fullWidth
                disabled
              />
            )}
          </Grid>
        )}

        {settings?.enableDealerCommisionPreview && (
          <Grid item xs={12}>
            {!(productLens.dealerCommissionValue |> get) && (
              <CustomTextField
                disabled
                label={t("Product.DealerCommissionForecast") + t("Product.NotCalculated")}
                fullWidth
              />
            )}

            {(productLens.dealerCommissionValue |> get) && (
              <CustomTextField
                isNumeric={true}
                startAdornment={<Typography variant="h6">{quote?.currencyCode}</Typography>}
                value={productLens.dealerCommissionValue |> get}
                onChange={productLens.dealerCommissionValue |> set |> onNumericTextFieldChange}
                label={t("Product.DealerCommissionForecast")}
                disabled={!(productLens.dealerCommissionIsManual |> get)}
                fullWidth
              />
            )}
          </Grid>
        )}

        <Grid item xs={12}>
          <Autocomplete
            key={"period"}
            label={t("Product.Period")}
            simpleValue
            value={product.periodicity || emptyString}
            options={productPeriodicities}
            onChange={productLens.periodicity |> set}
            noOptionsText={t("General.AutocompleteNoOptions")}
          />
        </Grid>

        <Grid item xs={12}>
          <DateTime
            disableToolbar={false}
            key={"firstInstallmentDate"}
            label={t("Product.FirstInstallmentDate")}
            value={productLens.firstInstallmentDate |> get}
            onChange={productLens.firstInstallmentDate |> set}
          />
        </Grid>

        {(productLens.canAddInsurance |> get) && autoAddedInsuranceTypes && allowInsurance && (
          <Grid item container alignItems="center">
            <Grid item xs={4} md={3}>
              <Typography>{t("Product.AddInsurance")}</Typography>
            </Grid>
            <Grid item xs={8} md={9}>
              <Checkbox
                checked={productLens.addInsurance |> get}
                onChange={productLens.addInsurance |> set |> onCheckBoxChange}
                size="small"
              />
            </Grid>
          </Grid>
        )}
        {product?.residualValueVisible && (
          <Grid item container alignItems="center">
            <Grid item xs={8}>
              {useResidualPercent && (
                <SimpleSliderWrapper
                  label={
                    product?.showAmountsWithVAT
                      ? t("Product.ResidualValueWithVAT")
                      : t("Product.ResidualValue") +
                        " | " +
                        t("DECIMAL2", { number: product?.residualValuePercent || 0 }) +
                        " " +
                        t("General.Percent") +
                        " (" +
                        quote?.currencyCode +
                        " " +
                        `${
                          product?.showAmountsWithVAT
                            ? t("DECIMAL2", { number: product?.residualValueAmountWithVAT || 0 })
                            : t("DECIMAL2", { number: product?.residualValueAmount || 0 })
                        }` +
                        ")"
                  }
                  min={product?.residualValueMinPercent}
                  max={product?.residualValueMaxPercentComputed}
                  value={product?.residualValuePercent}
                  onChange={onLensSliderChange(productLens.residualValuePercent)}
                  fullWidth={true}
                  aria-valuetext="balloonAmountSlider"
                  showSliderLimits={true}
                  error={!isValid(productValidation?.residualValuePercent)}
                  helperText={getErrors(productValidation?.residualValuePercent)}
                  decimalScale={2}
                  step={1}
                  classes={{ root: classes.sliderRoot, thumb: classes.sliderThumb }}
                  disabled={settings?.residualValueReadOnly}
                />
              )}
              {!useResidualPercent && (
                <CustomTextField
                  fullWidth
                  label={
                    product?.showAmountsWithVAT
                      ? t("Product.ResidualValueWithVAT")
                      : t("Product.ResidualValue") +
                        " | " +
                        t("DECIMAL2", { number: productLens.residualValuePercent |> get }) +
                        " " +
                        t("General.Percent") +
                        " (" +
                        quote.currencyCode +
                        " " +
                        `${
                          product?.showAmountsWithVAT
                            ? t("DECIMAL2", { number: productLens.residualValueAmountWithVAT |> get })
                            : t("DECIMAL2", { number: productLens.residualValueAmount |> get })
                        }` +
                        ")"
                  }
                  isNumeric={true}
                  onChange={
                    product?.showAmountsWithVAT
                      ? productLens.residualValueAmountWithVAT |> set |> onNumericTextFieldChange
                      : productLens.residualValueAmount |> set |> onNumericTextFieldChange
                  }
                  value={
                    product?.showAmountsWithVAT ? product?.residualValueAmountWithVAT : product?.residualValueAmount
                  }
                  startAdornment={<Typography variant="h6">{quote.currencyCode}</Typography>}
                  error={
                    !isValid(
                      product?.showAmountsWithVAT
                        ? productValidation?.residualValueAmountWithVAT
                        : productValidation?.residualValueAmount
                    )
                  }
                  helperText={getErrors(
                    product?.showAmountsWithVAT
                      ? productValidation?.residualValueAmountWithVAT
                      : productValidation?.residualValueAmount
                  )}
                  disabled={settings?.residualValueReadOnly}
                />
              )}
            </Grid>
            <Grid item xs={4}>
              <ButtonGroup className={classes.rightAlign}>
                <Button
                  id="balloon-percent"
                  variant="contained"
                  color={useResidualPercent === true ? "primary" : "secondary"}
                  onClick={handleActions}
                >
                  {t("General.Percent")}
                </Button>
                <Button
                  id="balloon-abs"
                  variant="contained"
                  color={useResidualPercent === false ? "primary" : "secondary"}
                  onClick={handleActions}
                >
                  {t("General.Abs")}
                </Button>
              </ButtonGroup>
            </Grid>
          </Grid>
        )}

        {(settingsLens.productServiceTypes |> get).length > 0 && (
          <Grid item xs={12}>
            <Autocomplete
              valueKey="serviceTypeId"
              labelKey="serviceName"
              label={t("EditProduct.Services")}
              options={settingsLens.productServiceTypes |> get}
              onChange={handleAddService}
              noOptionsText={t("General.AutocompleteNoOptions")}
            />
          </Grid>
        )}

        {(productLens.quoteProductServices |> sequence).map((serviceLens, index) => (
          <Grid item xs={12} key={index}>
            <ServiceContainer
              serviceLens={serviceLens}
              settingsLens={settingsLens}
              currencyCode={quote.currencyCode}
              serviceValidation={productValidation.quoteProductServices[index]}
              onDeleteService={handleDeleteService}
            />
          </Grid>
        ))}
      </Grid>
    </SimplebarWrapper>
  );
}

AdvancedEditProductParams.propTypes = {
  quote: PropTypes.object.isRequired,
  productLens: PropTypes.object.isRequired,
  productDirtyInfo: PropTypes.object.isRequired,
  productValidation: PropTypes.object.isRequired,
  productScheduleTypes: PropTypes.array.isRequired,
  productPeriodicities: PropTypes.array.isRequired,
  settingsLens: PropTypes.object.isRequired,
  autoAddedInsuranceTypes: PropTypes.bool.isRequired,
};

export default AdvancedEditProductParams;
